
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { measurementTypeFacts } from "./data/measurement-type-facts";
import { unitSpecificFacts } from "./data/unit-specific-facts";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface HistoricalFactProps {
  measurementType: string;
  fromUnit?: string;
  toUnit?: string;
}

interface SelectedFact {
  text: string;
  unit?: string;
}

export function HistoricalFact({ measurementType, fromUnit, toUnit }: HistoricalFactProps) {
  const [currentFact, setCurrentFact] = useState<SelectedFact | null>(null);

  useEffect(() => {
    if (!measurementType) return;

    let selectedFact: SelectedFact | null = null;

    // Try to get facts for each unit (prioritizing more detailed unit-specific facts)
    const units = [fromUnit, toUnit].filter(Boolean);
    
    for (const unit of units) {
      // Convert unit name to lowercase and handle special characters
      const normalizedUnit = unit!.toLowerCase()
        .replace(/°/g, '')  // Remove degree symbols
        .replace(/\s+/g, '-'); // Replace spaces with hyphens
      
      const unitData = unitSpecificFacts[normalizedUnit];
      
      if (unitData?.history) {
        // Prefer using the rich history text if available
        selectedFact = {
          text: unitData.history.split('.')[0] + '.',  // Take first sentence of history
          unit: unitData.name
        };
        break;
      } else if (unitData?.funFacts?.length) {
        // Fall back to fun facts if no history
        selectedFact = {
          text: unitData.funFacts[Math.floor(Math.random() * unitData.funFacts.length)],
          unit: unitData.name
        };
        break;
      }
    }

    // Only fall back to measurement type facts if no unit-specific content found
    if (!selectedFact && measurementType) {
      const typeFacts = measurementTypeFacts[measurementType];
      if (typeFacts?.length) {
        selectedFact = {
          text: typeFacts[Math.floor(Math.random() * typeFacts.length)]
        };
      }
    }

    setCurrentFact(selectedFact);
  }, [measurementType, fromUnit, toUnit]);

  if (!currentFact?.text) return null;

  return (
    <div className="bg-white/50 backdrop-blur-sm rounded-lg shadow-lg p-8 border border-manuscript-secondary transition-all duration-300 hover:shadow-xl">
      <h2 className="text-2xl font-cinzel text-manuscript-primary mb-4">Did You Know?</h2>
      <p className="text-manuscript-primary/80 italic animate-fade-in mb-4">
        {currentFact.text}
      </p>
      {currentFact.unit && (
        <Link to={`/history/${currentFact.unit.toLowerCase()}`}>
          <Button 
            variant="outline" 
            className="mt-2 text-manuscript-primary border-manuscript-primary hover:bg-manuscript-primary hover:text-white group"
          >
            Learn more about the {currentFact.unit}
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      )}
    </div>
  );
}
